import { ComponentType } from '@angular/cdk/portal';
import { AfterViewChecked, AfterViewInit, Component, effect, inject, Inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogClose } from '@angular/material/dialog';
import { FileSubTypeByFileType, PayerRiskAdjustmentPolicy, PayerRiskAdjustmentPolicyUpdate } from '../../models/payer-config-item';
import { PreferredActionFilterSelectComponent } from '../../components/field-components/preferred-action-filter-select/preferred-action-filter-select.component';
import { formatDate } from '@angular/common';
import { MatDatepickerInputEvent, MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { FieldFilterService } from '../../services/filters/field-filter.service';
import { FilterSelectItem } from '../../models/filter-select-item';
import { FilterSearchSelectComponent } from '../../components/field-components/filter-search-select/filter-search-select.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { PayerConfigService } from '../../services/payer/payer-config.service';
import { AddUpdatePayerRiskAdjustmentPoliciesRequest } from '../../models/add-update-payer-risk-adjustment-policies-request';
import { PayerEditStore } from '../../store/payer-edit.store';
import { FileTypes } from '../../models/constants/file-types';
import { ModelVersionResult } from '../../models/model-version-result';
import { LeadTimeFilterSelectComponent } from '../../components/field-components/lead-time-filter-select/lead-time-filter-select.component';
import { YesNoToggleComponent } from '../../components/field-components/static/yes-no-toggle/yes-no-toggle.component';
import { DialogHeaderComponent } from '../../components/layout-components/dialog-header/dialog-header.component';
import { FormSectionComponent } from '../../components/layout-components/form-section/form-section.component';
import { LabeledFieldComponent } from '../../components/layout-components/labeled-field/labeled-field.component';
import { SettingRowComponent } from '../../components/layout-components/setting-row/setting-row.component';
import { DialogFooterComponent } from '../../components/layout-components/dialog-footer/dialog-footer.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { PayerPolicyFunctions } from '../../helpers/payer-policy-functions';

export class AddUpdateRiskPolicyDialogModel {
  readonly component: ComponentType<any> = AddUpdateRiskPolicyDialogComponent;
  panelClass?: string | string[] = 'form-dialog';
  autoFocus?: boolean = false;
  data?: any;
  disableClose? = true;
  width? = '1040px';
  maxWidth? = '95dvw';
  maxHeight? = '90dvh';

  constructor(data: any) {
    this.data = data;
  }
}

@Component({
    selector: "app-add-update-risk-policy-dialog",
    templateUrl: "./add-update-risk-policy-dialog.component.html",
    styleUrls: ["./add-update-risk-policy-dialog.component.scss"],
    imports: [
        CdkScrollable,
        MatDialogContent,
        MatFormField,
        MatInput,
        MatDatepickerInput,
        FormsModule,
        ReactiveFormsModule,
        MatDatepickerToggle,
        MatSuffix,
        MatDatepicker,
        MatError,
        PreferredActionFilterSelectComponent,
        FilterSearchSelectComponent,
        MatButton,
        MatDialogClose,
        LeadTimeFilterSelectComponent,
        YesNoToggleComponent,
        DialogHeaderComponent,
        FormSectionComponent,
        LabeledFieldComponent,
        SettingRowComponent,
        DialogFooterComponent,
        MatCheckbox,
        MatIcon
    ]
})
export class AddUpdateRiskPolicyDialogComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  form: FormGroup;

  serviceYearStartControl = new FormControl(null, Validators.required);
  serviceYearEndControl = new FormControl(null, Validators.required);
  notesControl = new FormControl("");
  hasRiskAdjustmentControl = new FormControl<string | null>("Yes", Validators.required);
  hasManualOmitControl = new FormControl<string | null>("No", Validators.required);
  receivingMAOControl = new FormControl<string | null>("No", Validators.required);
  receivingMORControl = new FormControl<string | null>("No", Validators.required);
  acceptsAsmDeleteControl: FormControl<string | null>;

  serviceYearStartControlName = "serviceYearStart";
  serviceYearEndControlName = "serviceYearEnd";
  hasRiskAdjustmentControlName = "hasRiskAdjustment";
  hasManualOmitControlName = "hasManualOmit";
  preferredActionControlName = "preferredAction";
  asmTemplateControlName = "asmTemplateControl";
  receivingMORSelectControlName = "receivingMORSelectControl";
  receivingMAOSelectControlName = "receivingMAOSelectControl";
  modelVersionSelectControlName = "modelVersionSelectControl";
  acceptsAsmDeleteControlName = "acceptsAsmDelete";

  readonly leadTimeHint = "Indicates how many days prior to the Medicare submission deadline a payer must receive files.";

  asmTemplateEnabled = false;

  item: PayerRiskAdjustmentPolicy;
  asmTemplateItems: FilterSelectItem[] = [];

  // Copy mode: every setting is carried over from data.item except the service years, which the
  // user must enter, and the copy saves as a new policy.
  readonly isCopy: boolean;
  copiedTemplateIds: string[] = [];
  copyTemplatesControl = new FormControl(true);

  // Range of another of the payer's policies that the entered service years overlap
  // (only checked when the caller passes data.existingPolicies).
  overlappingRange = "";

  isReceivingMOR: boolean = false;
  isReceivingMAO: boolean = false;

  leadTime: number | null = null;
  private fieldsInitialized = false;
  private viewChildrenReady = signal(false);
  private modelVersionsLoaded = signal(false);
  private asmTemplatesLoaded = signal(false);
  private subscriptions = new Subscription();

  readonly payerEditStore = inject(PayerEditStore);

  @ViewChild("riskPolicyPaSelect") paSelect?: PreferredActionFilterSelectComponent;
  @ViewChild("asmTemplate")
  asmTemplateSelect?: FilterSearchSelectComponent<FilterSelectItem>;
  @ViewChild("modelVersionSelect")
  modelVersionSelect?: FilterSearchSelectComponent<FilterSelectItem>;
  modelVersionResults?: ModelVersionResult[];
  @ViewChild(LeadTimeFilterSelectComponent) leadTimeSelect?: LeadTimeFilterSelectComponent;

  constructor(
    public dialogRef: MatDialogRef<AddUpdateRiskPolicyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private filterService: FieldFilterService,
    private configService: PayerConfigService,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({});
    this.form.addControl(
      this.serviceYearStartControlName,
      this.serviceYearStartControl
    );
    this.form.addControl(
      this.serviceYearEndControlName,
      this.serviceYearEndControl
    );

    this.form.addControl(this.hasRiskAdjustmentControlName, this.hasRiskAdjustmentControl);
    this.form.addControl(this.hasManualOmitControlName, this.hasManualOmitControl);
    this.form.addControl(this.receivingMAOSelectControlName, this.receivingMAOControl);
    this.form.addControl(this.receivingMORSelectControlName, this.receivingMORControl);

    this.item = this.data.item;
    this.isCopy = this.data.mode === "copy" && !!this.item;
    this.copiedTemplateIds = this.isCopy ? PayerPolicyFunctions.asmTemplateIds(this.item) : [];
    this.form.addValidators(this.overlapValidator);
    this.asmTemplateEnabled =
      this.item?.PreferredActionSubmissionType?.ID == 4;

    // Starts from the policy's stored value; new policies and a stored null default to Yes
    this.acceptsAsmDeleteControl = new FormControl<string | null>(
      this.toYesNo(this.item?.AcceptsASMDelete) ?? "Yes"
    );
    this.form.addControl(this.acceptsAsmDeleteControlName, this.acceptsAsmDeleteControl);

    effect(() => {
      if (
        !this.fieldsInitialized &&
        (this.data.mode === "edit" || this.isCopy) &&
        this.viewChildrenReady() &&
        this.modelVersionsLoaded() &&
        (!this.asmTemplateEnabled || this.asmTemplatesLoaded())
      ) {
        this.fieldsInitialized = true;
        this.setFields();
      }
    });
  }

  ngOnInit(): void {
    //ASM Templates
    this.subscriptions.add(
      this.filterService
        .getExportTemplatesByTypeAndPayer("1")
        .subscribe((result) => {
          this.asmTemplateItems = result;
          this.asmTemplatesLoaded.set(true);
        })
    );

    //Model Versions
    this.subscriptions.add(
      this.filterService
        .getModelVersionDropdownItemsByFileType(FileTypes.MOR as number)
        .subscribe((result) => {
          this.modelVersionResults = result;
          const filterItems = result.map((i) => {
            return {
              ID: i.FileSubTypeID,
              Description: i.Value,
              LinkAssociations: [],
              Selected: false,
            };
          });
          if (this.modelVersionSelect) {
            this.modelVersionSelect.options = filterItems;
          }
          this.modelVersionsLoaded.set(true);
        })
    );
  }

  ngAfterViewInit(): void {
    // Subscribe to the form control that LeadTimeFilterSelectComponent adds
    const leadTimeControl = this.form.get('lead-time-select');
    if (leadTimeControl) {
      this.subscriptions.add(
        leadTimeControl.valueChanges.subscribe((value) => {
          this.leadTime = value;
        })
      );
      // Also set initial value
      this.leadTime = leadTimeControl.value;
    }
  }

  ngAfterViewChecked(): void {
    // Assign options to selects once ViewChild is available and data is loaded
    if (this.asmTemplateEnabled && this.asmTemplateSelect && this.asmTemplateItems.length > 0) {
      this.asmTemplateSelect.options = this.asmTemplateItems;
    }

    // Initialize fields once after all ViewChild components are available
    if (!this.viewChildrenReady() &&
        this.modelVersionSelect &&
        this.paSelect &&
        (!this.asmTemplateEnabled || this.asmTemplateSelect)) {
      this.viewChildrenReady.set(true);
    }
  }

  compareById = (a: any, b: any) => a?.ID === b?.ID;

  setFields() {
    
    if (this.data.mode == "edit" || this.isCopy) {
      // A copy keeps every setting but needs a new service year range.
      if (!this.isCopy) {
        this.serviceYearStartControl.setValue(
          new Date(this.item.ServiceYearStart)
        );
        this.serviceYearEndControl.setValue(new Date(this.item.ServiceYearEnd));
      }
      this.form
        .get(this.hasRiskAdjustmentControlName)
        .setValue(this.item.HasRiskAdjustment ? "Yes" : "No");
      this.form
        .get(this.hasManualOmitControlName)
        .setValue(this.item.HasManualOmit ? "Yes" : "No");
      this.form
        .get(this.receivingMORSelectControlName)
        ?.setValue(
          this.item.FileSubTypesByFileType &&
            this.item.FileSubTypesByFileType?.length > 0 &&
            this.item.FileSubTypesByFileType?.filter((i) => i.FileTypeID == 1)
              .length > 0
            ? "Yes"
            : "No"
        );
      this.form
        .get(this.receivingMAOSelectControlName)
        ?.setValue(
          this.item.FileSubTypesByFileType &&
            this.item.FileSubTypesByFileType?.length > 0 &&
            this.item.FileSubTypesByFileType?.filter((i) => i.FileTypeID == 2)
              .length > 0
            ? "Yes"
            : "No"
        );
      const morModels = this.item.FileSubTypesByFileType?.filter(
        (i) => i.FileTypeID == 1
      );
      const preset: FilterSelectItem[] = [];
      this.modelVersionSelect.options.forEach((o) => {
        const found = morModels?.find((i) => i.FileSubTypeID == o.ID);
        if (found) {
          o.Selected = true;
          preset.push(o);
        }
      });
      this.form.get(this.modelVersionSelectControlName)?.setValue(preset);

      this.paSelect.setSingleModeValue(this.item.PreferredActionSubmissionType);
      this.notesControl.setValue(this.item.Note);
      this.acceptsAsmDeleteControl.setValue(this.toYesNo(this.item.AcceptsASMDelete) ?? "Yes");
      if (this.asmTemplateEnabled) {
        const asmIds = this.item.ASMTemplateIDs
          ? this.item.ASMTemplateIDs.split(',').map((i) => i.trim()).filter(Boolean)
          : [];
        const selectedAsmTemplates = this.asmTemplateItems.filter((i) =>
          asmIds.includes(i.ID.toString())
        );
        this.form
          .get(this.asmTemplateControlName)
          ?.setValue(selectedAsmTemplates);
      }
      this.syncManualOmitEnabled();
    }
  }

  // Unchecking Copy Templates clears the copied ASM templates; re-checking restores them.
  onCopyTemplatesChange(checked: boolean) {
    const selected = checked
      ? this.asmTemplateItems.filter((i) => this.copiedTemplateIds.includes(i.ID.toString()))
      : [];
    this.form.get(this.asmTemplateControlName)?.setValue(selected);
  }

  private overlapValidator = (): ValidationErrors | null => {
    const overlap = PayerPolicyFunctions.findOverlappingPolicy(
      this.serviceYearStartControl.value,
      this.serviceYearEndControl.value,
      this.data.existingPolicies,
      this.data.mode === "edit" ? this.item?.PolicyID : null
    );
    this.overlappingRange = overlap ? `${overlap.ServiceYearStart} – ${overlap.ServiceYearEnd}` : "";
    return overlap ? { serviceYearOverlap: true } : null;
  };

  onRiskPolicyChanged(value: string) {
    if (value == "No") {
      this.form.get(this.hasManualOmitControlName).setValue("");
      this.form.get(this.preferredActionControlName)?.setValue("");
      this.hideAsmFields();
    }

    if (value == "Yes" && !this.form.get(this.hasManualOmitControlName).value) {
      this.form.get(this.hasManualOmitControlName).setValue("No");
    }

    this.syncManualOmitEnabled();
  }

  onManualOmitChanged(value: string) {
    if (value == "Yes") {
      this.form.get(this.preferredActionControlName)?.setValue("");
      this.hideAsmFields();
    }
  }

  // Manual omit only applies when the policy has risk adjustment
  private syncManualOmitEnabled() {
    if (this.hasRiskAdjustmentControl.value == "No") {
      this.hasManualOmitControl.disable();
    } else {
      this.hasManualOmitControl.enable();
    }
  }

  // Hiding the ASM-only fields destroys the template select, so drop its control too; the select
  // adds a fresh one when shown again. Accepts ASM Delete keeps its value so re-showing restores it.
  private hideAsmFields() {
    this.asmTemplateEnabled = false;
    this.form.removeControl(this.asmTemplateControlName);
  }

  private toYesNo(value: boolean | null | undefined): string | null {
    if (value === true) {
      return "Yes";
    }
    if (value === false) {
      return "No";
    }
    return null;
  }

  private toBoolean(value: string | null | undefined): boolean | null {
    if (value === "Yes") {
      return true;
    }
    if (value === "No") {
      return false;
    }
    return null;
  }

  onStartDateChange(event: MatDatepickerInputEvent<Date>) {
    const endValue = this.serviceYearEndControl.value;
    const startValue = event.value;

    if (startValue > endValue) {
      this.serviceYearEndControl.reset();
      this.serviceYearEndControl.markAsTouched();
    }
  }

  // value is null when the panel closes with nothing selected.
  onPreferredActionChanged(value: FilterSelectItem | null) {
    //Check for ASM Template
    if (value?.ID == 4) {
      this.asmTemplateEnabled = true;
      if (this.asmTemplateSelect) {
        this.asmTemplateSelect.options = this.asmTemplateItems;
      }
    } else {
      this.hideAsmFields();
    }
  }

  onReceivingMORChange(value: string) {
    this.isReceivingMOR = value === "Yes";
  }

  onReceivingMAOChange(value: string) {
    this.isReceivingMAO = value === "Yes";
  }

  onSaveClick() {
    const newItem = new PayerRiskAdjustmentPolicyUpdate();
    const request: AddUpdatePayerRiskAdjustmentPoliciesRequest =
      new AddUpdatePayerRiskAdjustmentPoliciesRequest();
    request.PayerName = this.payerEditStore.currentPayerItem().PayerName;
    newItem.PayerID = this.payerEditStore.currentPayerItem().PayerID;
    newItem.PolicyID = this.item && !this.isCopy ? this.item.PolicyID : 0;
    newItem.ServiceYearStart = formatDate(
      this.serviceYearStartControl.value,
      "M/d/yyyy",
      "en"
    );
    newItem.ServiceYearEnd = formatDate(
      this.serviceYearEndControl.value,
      "M/d/yyyy",
      "en"
    );
    newItem.HasRiskAdjustment = this.hasRiskAdjustmentControl.value === "Yes";
    newItem.HasManualOmit = this.hasManualOmitControl.value === "Yes";
    newItem.PreferredActionSubmissionTypeID =
      this.form.get(this.preferredActionControlName)?.value?.ID || null;
    newItem.PreferredActionName = this.form.get(this.preferredActionControlName)?.value?.Description || null;
    newItem.Note = this.notesControl.value ?? "";
    newItem.PayerValidationTemplateID = null;

    if (this.asmTemplateEnabled) {
      const values = this.form.get(this.asmTemplateControlName)?.value;
      let asmTemplateIDs: string | null = null;
      if (Array.isArray(values) && values.length > 0) {
        asmTemplateIDs = values.map(i => i.ID).join(",");
      }
      newItem.ASMTemplateIDs = asmTemplateIDs;
      newItem.AcceptsASMDelete = this.toBoolean(this.acceptsAsmDeleteControl.value);
    } else {
      // ASM-only fields are hidden for other submission types
      newItem.ASMTemplateIDs = null;
      newItem.AcceptsASMDelete = null;
    }
    newItem.FileSubTypesByFileType = [];
    if (this.form.get(this.receivingMORSelectControlName)?.value == "Yes") {
      const mapped = this.form.get(this.modelVersionSelectControlName)?.value.map((i) => {
        return {
          FileTypeID: FileTypes.MOR as number,
          FileSubTypeID: i.ID,
        } as FileSubTypeByFileType;
      });
      
      newItem.FileSubTypesByFileType.push(...mapped);
    }
    if (this.form.get(this.receivingMAOSelectControlName)?.value == "Yes") {
      newItem.FileSubTypesByFileType.push({
        FileTypeID: FileTypes.MAO as number
      } as FileSubTypeByFileType);
    }
    if (this.leadTime !== null && this.leadTime !== undefined) {
      newItem.SubmissionLeadTimeDays = this.leadTime;
    }

    newItem.ReceivingMAO = this.toBoolean(this.receivingMAOControl.value);
    newItem.ReceivingMOR = this.toBoolean(this.receivingMORControl.value);
    // Only log MOR versions when the payer is receiving MOR; the select can still hold stale picks
    newItem.MORVersions = newItem.ReceivingMOR
      ? (this.form.get(this.modelVersionSelectControlName)?.value ?? []).map(i => i.Description).join(", ") || null
      : null;

    request.RiskPolicies.push(newItem);
    
    this.subscriptions.add(
      this.configService
        .addUpdatePayerRiskAdjustmentPolicies(request)
        .subscribe({
          next: () => { this.dialogRef.close(newItem) },
          error: (err) => {
            // most common errors handled by interceptor
          }
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.form.reset();
  }
}
